import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "@assets/icons/icons";
import { sidebarItems } from "@constants/sidebarItems";

export const Sidebar = ({ onCollapseChange }) => {
  const [openItem, setOpenItem] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("uv_sidebar_collapsed");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const location = useLocation();

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    try {
      localStorage.setItem("uv_sidebar_collapsed", JSON.stringify(next));
    } catch { }
    if (onCollapseChange) onCollapseChange(next);
    if (next) setOpenItem(null);
  };

  useEffect(() => {
    if (onCollapseChange) onCollapseChange(isCollapsed);
  }, []);

  const handleItemClick = (itemName) => {
    if (isCollapsed) return;
    setOpenItem(openItem === itemName ? null : itemName);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <aside
      className={`hidden md:block group fixed top-0 mt-[64.8px] left-0 z-40 h-screen transition-[width,transform] duration-300 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 ${isCollapsed ? "w-16" : "w-64"
        }`}
      aria-label="Sidebar"
    >
      {/* Collapse button */}
      <button
        type="button"
        onClick={toggleCollapse}
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-6 flex items-center justify-center w-6 h-6 rounded-full border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 shadow-sm transition-all z-50"
      >
        {isCollapsed ? (
          <ChevronRight className="w-3.5 h-3.5" />
        ) : (
          <ChevronLeft className="w-3.5 h-3.5" />
        )}
      </button>

      <div
        className={`h-full ${isCollapsed ? "px-2" : "px-3"
          } py-4 overflow-y-auto bg-white dark:bg-gray-900 custom-scrollbar pb-24`}
      >
        <ul className="space-y-1 font-medium">
          {sidebarItems.map((item) => (
            <li key={item.name}>
              {item.children.length === 0 ? (
                <Link
                  to={item.href}
                  className={`flex items-center p-2 rounded-lg group transition-all duration-200 ${isActive(item.href)
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                    } ${isCollapsed ? "justify-center" : ""}`}
                >
                  {item.icon}
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 ms-3 whitespace-nowrap">
                        {item.name}
                      </span>
                      {item.isPro && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800 rounded-full dark:bg-gray-700 dark:text-gray-300">
                          PRO
                        </span>
                      )}
                      {item.badge && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-blue-200 text-blue-800 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              ) : (
                <>
                  {/* Courses button */}
                  <button
                    type="button"
                    onClick={() => handleItemClick(item.name)}
                    className={`text-left flex items-center w-full p-2 rounded-lg group transition-all duration-200 ${isActive(item.href)
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                      } ${isCollapsed ? "justify-center" : ""}`}
                  >
                    {item.icon}
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 ms-3">{item.name}</span>
                        {item.isPro && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800 rounded-full dark:bg-gray-700 dark:text-gray-300">
                            PRO
                          </span>
                        )}
                        <ChevronDown
                          className={`ml-auto w-4 h-4 transform transition-transform ${openItem === item.name ? "rotate-180" : ""
                            }`}
                        />
                      </>
                    )}
                  </button>

                  {/* Submenu */}
                  {!isCollapsed && (
                    <ul
                      className={`pl-8 mt-1 space-y-1 overflow-hidden transition-all duration-300 ${openItem === item.name
                        ? "max-h-96 opacity-100"
                        : "max-h-0 opacity-0"
                        }`}
                    >
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <Link
                            to={child.href}
                            className={`flex items-center p-2 rounded-lg text-sm transition-all duration-200 ${isActive(child.href)
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                              : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                              }`}
                          >
                            {child.icon && (
                              <span className="mr-2">{child.icon}</span>
                            )}
                            <span className="flex-1">{child.name}</span>
                            {child.badge && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-blue-200 text-blue-800 rounded-full">
                                {child.badge}
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};
